"use client"
import Image from "next/image"
import { useRef, useState } from "react";
import Reveal from "../../enterprise/components/Reveal";
import { motion } from "motion/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import "swiper/css";
import { useTranslations } from "next-intl";

const CollaborationSection = () => {

    const t = useTranslations('governance');
    const data = [
        {
            number: "01",
            title: t('proposal'),
            subTitle: t('anyone-can-submit-ideas-or-changes'),
            imgUrl: "/images/backgrounds/onchain-gov.png",
        },
        {
            number: "02",
            title: t('voting'),
            subTitle: t('token-stakers-decide-outcomes'),
            imgUrl: "/images/backgrounds/onchain-gov2.png",
        },
        {
            number: "03",
            title: t('execution'),
            subTitle: t('smart-contracts-enforce-results-automatically'),
            imgUrl: "/images/backgrounds/onchain-gov3.png",
        },
    ]
    const [activeIdx, setActiveIdx] = useState(0);
    const ref = useRef(null);

  return (
    <section className="min-h-screen max-lg:h-auto max-w-[1440px] mx-auto px-[78px] max-xl:px-10 max-sm:px-4 py-[120px] max-lg:py-[56px]">
        <div className="flex max-sm:flex-col-reverse gap-2 items-center justify-between">
            <div className="max-w-[520px] max-lg:max-w-[450px]">
                <Reveal 
                    delay={0.1} 
                    duration={0.8} 
                    direction="left"
                    withBlur={true}
                    blurAmount={8}
                >
                    <h2 className="text-[48px]/[56px] max-lg:text-[32px]/[40px] max-sm:text-[24px]/[32px] tracking-[-1px] text-[#202020]">{t('where-innovation-meets')} <span className="text-[#06f]">{t('collaboration')}</span></h2>
                </Reveal>
                <Reveal 
                    delay={0.5} 
                    duration={0.8} 
                    direction="left"
                    withBlur={true}
                    blurAmount={8}
                >
                    <p className="text-lg max-lg:text-sm font-light text-[#202020] mt-6">{t('discover-the-easiest-ways-to-join-contribute-and-access-the-tools-that-matter-most')}</p>
                </Reveal>
            </div>
            <motion.div
                initial={{ opacity: 0, translateY: 40 }}
                whileInView={{ opacity: 1, translateY: 0 }}
                transition={{ duration: 1, ease: 'easeOut' }}
                viewport={{ once: true }} 
                className="w-1/3 max-md:w-full relative"   
            >
                <Image src={"/images/icons/chain-gov.png"} width={400} height={400} alt="image" className="w-full object-center"/>
            </motion.div>
            
        </div>
        <div className="mt-20 flex justify-between items-center max-lg:hidden">
            <motion.div
                key={data[activeIdx].imgUrl}
                initial={{ opacity: 0, translateY: 40 }}
                animate={{ opacity: 1, translateY: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="flex justify-center items-center max-h-[400px]"
            >
                <Image
                    src={data[activeIdx].imgUrl}
                    width={500}
                    height={300}
                    alt={data[activeIdx].title}
                    className="rounded-xl"
                />
            </motion.div>
            {/* desktop */}
            <div className="">
                <p className="font-ibm-plex-mono text-base text-[#06f] font-normal mt-14 border-l border-[#06f] pl-[66px] mb-8">{t('on-chain-governance-lifecycle')}</p>
                {data.map((item, index) => (
                    <motion.div
                        initial={{ opacity: 0, translateY: 40 }}
                        whileInView={{ opacity: 1, translateY: 0 }}
                        transition={{delay: 0.3 * index , duration: 1, ease: 'easeOut' }}
                        viewport={{ once: true }}
                        key={index}            
                    >
                    <div >
                        <div onClick={() => setActiveIdx(index)} className="flex gap-4 rounded-xl my-1 cursor-pointer">
                            <div className={`w-[52px] h-[52px] aspect-square flex items-center justify-center rounded-xl shadow-[inset_0_6px_12px_rgba(0,0,0,0.15)] transition-colors duration-300 ease-out
                                    ${activeIdx === index? "bg-[#06f] text-white" : "bg-white text-[#06f]"}
                                `}>
                                {item.number}
                            </div>
                            <div>
                                <p className="text-2xl text-[#202020]">{item.title}</p>
                                <p className="text-base text-[#646464] font-light">{item.subTitle}</p>
                            </div>
                        </div>
                        <div className="flex justify-center w-[52px]">
                            {item.number !== "03" && (
                                <Image src={"/images/icons/arrow-down-dash.svg"} width={7} height={10} alt="arrow"/>
                            )}
                        </div>
                    </div>
                    </motion.div>
                ))}
            </div>
        </div>
        {/* tablet -mobile */}
        <div className="lg:hidden">
            <Swiper
            modules={[FreeMode]}
            freeMode={{ enabled: true }}
            slidesPerView="auto"
            spaceBetween={16}
            grabCursor
            className="!overflow-visible mt-10"
        >
            {data.map((item, index) => (
            <SwiperSlide key={index} className="!w-auto">
                <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                    duration: 0.5,
                    ease: "easeOut",
                    delay: index * 0.1,
                }}
                viewport={{ once: true }}
                onClick={() => setActiveIdx(index)}
                className={`p-5 max-w-[310px] ${activeIdx === index ? "bg-[#F3F8FF]" : ""}`}
                >
                    <div className=" w-[270px] aspect-square relative">
                        <Image src={item.imgUrl} fill alt="thumbnail" className="object-cover"/>
                    </div>
                    <div className={`mt-6 w-[52px] h-[52px] aspect-square flex items-center justify-center rounded-xl shadow-[inset_0_6px_12px_rgba(0,0,0,0.15)] transition-colors duration-300 ease-out
                            ${activeIdx === index? "bg-[#06f] text-white" : "bg-white text-[#06f]"}
                        `}>
                        {item.number}
                    </div>
                    <div className="mt-5">
                        <p className="text-2xl max-sm:text-xl text-[#202020]">{item.title}</p>
                        <p className="text-base max-sm:text-sm text-[#646464] font-light">{item.subTitle}</p>
                    </div>
                </motion.div>
            </SwiperSlide>
            ))}
        </Swiper>
        </div>
    </section>
  )
}

export default CollaborationSection